/**
 * Загрузка машин и цепочки заправок + отметки «Готово» / «Пропустить».
 *
 * Прогресс по шагам ведёт сервер, но дублируем его в localStorage: у колонки
 * связь пропадает регулярно, а пройденный путь терять нельзя.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Car, GasStation, LatLon, Plan, PlannerSettings } from '@cdf/shared';

import { ApiRequestError, api, type PlanBundle, type StopProgress } from '../api/client';

const PROGRESS_KEY = 'cdf.progress.v1';

interface StoredProgress {
  planId: string;
  done: number[];
  skipped: number[];
}

function isIndex(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function readProgress(): StoredProgress | null {
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Partial<StoredProgress>;
    if (typeof record.planId !== 'string') return null;
    return {
      planId: record.planId,
      done: Array.isArray(record.done) ? record.done.filter(isIndex) : [],
      skipped: Array.isArray(record.skipped) ? record.skipped.filter(isIndex) : [],
    };
  } catch {
    return null;
  }
}

function writeProgress(progress: StoredProgress): void {
  try {
    window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    /* Хранилище может быть недоступно — прогресс просто останется в памяти. */
  }
}

function splitProgress(progress: Record<number, StopProgress>): {
  done: Set<number>;
  skipped: Set<number>;
} {
  const done = new Set<number>();
  const skipped = new Set<number>();
  for (const [key, state] of Object.entries(progress)) {
    const index = Number(key);
    if (state === 'done') done.add(index);
    else if (state === 'skipped') skipped.add(index);
  }
  return { done, skipped };
}

export type PlanStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface PlanData {
  status: PlanStatus;
  plan: Plan | null;
  stations: GasStation[];
  cars: Car[];
  rejectedCount: number;
  fetchedAtISO: string | null;
  mock: boolean;
  error: ApiRequestError | null;
}

export interface PlanApi extends PlanData {
  done: ReadonlySet<number>;
  skipped: ReadonlySet<number>;
  /** Идёт ли попытка поднять сохранённый на сервере план. */
  restoring: boolean;
  /** Индекс текущего шага или null, если цепочка пройдена. */
  currentIndex: number | null;
  load(start: LatLon, settings: PlannerSettings): Promise<void>;
  /** Поднять активный план сессии, если сервер его помнит. */
  restore(radiusKm: number): Promise<void>;
  markDone(index: number): Promise<void>;
  markSkipped(index: number): Promise<void>;
  /** План пришёл из монитора (событие plan_updated). */
  replacePlan(plan: Plan): void;
}

const EMPTY: PlanData = {
  status: 'idle',
  plan: null,
  stations: [],
  cars: [],
  rejectedCount: 0,
  fetchedAtISO: null,
  mock: false,
  error: null,
};

export function usePlan(): PlanApi {
  const [data, setData] = useState<PlanData>(EMPTY);
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  const [skipped, setSkipped] = useState<ReadonlySet<number>>(new Set());
  const [restoring, setRestoring] = useState(true);
  const abortRef = useRef<AbortController | null>(null);
  const planIdRef = useRef<string | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const persist = useCallback(
    (nextDone: ReadonlySet<number>, nextSkipped: ReadonlySet<number>) => {
      const planId = planIdRef.current;
      if (!planId) return;
      writeProgress({ planId, done: [...nextDone], skipped: [...nextSkipped] });
    },
    [],
  );

  /**
   * Прогресс: сервер главнее, но если он молчит — берём локальную копию
   * того же плана.
   */
  const adoptProgress = useCallback(
    (bundle: PlanBundle) => {
      const planId = bundle.plan.id;
      const fromServer = splitProgress(bundle.progress);
      planIdRef.current = planId;

      if (fromServer.done.size > 0 || fromServer.skipped.size > 0) {
        setDone(fromServer.done);
        setSkipped(fromServer.skipped);
        persist(fromServer.done, fromServer.skipped);
        return;
      }

      const stored = readProgress();
      if (stored && stored.planId === planId) {
        setDone(new Set(stored.done));
        setSkipped(new Set(stored.skipped));
        return;
      }

      setDone(new Set());
      setSkipped(new Set());
      writeProgress({ planId, done: [], skipped: [] });
    },
    [persist],
  );

  const applyBundle = useCallback(
    (bundle: PlanBundle, extra: Partial<PlanData> = {}) => {
      adoptProgress(bundle);
      setData((prev) => ({
        ...prev,
        status: 'ready',
        plan: bundle.plan,
        stations: bundle.stations,
        mock: bundle.mock || prev.mock,
        error: null,
        ...extra,
      }));
    },
    [adoptProgress],
  );

  const restore = useCallback(
    async (radiusKm: number) => {
      try {
        const bundle = await api.getActivePlan();
        if (!bundle) return;
        applyBundle(bundle);

        // Машины вокруг старта восстановленного плана: без них на карте
        // не видно, что рядом есть и другие варианты.
        try {
          const cars = await api.getCars({
            lat: bundle.plan.start.lat,
            lon: bundle.plan.start.lon,
            radiusKm,
          });
          setData((prev) => ({
            ...prev,
            cars: cars.cars,
            rejectedCount: cars.rejected.length,
            fetchedAtISO: cars.fetchedAtISO,
            mock: prev.mock || cars.mock,
          }));
        } catch {
          // Машины — украшение; план важнее и он уже есть.
        }
      } catch {
        // Нет плана или сервер не в духе — просто построим новый.
      } finally {
        setRestoring(false);
      }
    },
    [applyBundle],
  );

  const load = useCallback(
    async (start: LatLon, settings: PlannerSettings) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setRestoring(false);
      setData((prev) => ({ ...prev, status: 'loading', error: null }));

      try {
        // Машины и план — независимые запросы, тянем параллельно.
        const [cars, bundle] = await Promise.all([
          api.getCars(
            { lat: start.lat, lon: start.lon, radiusKm: settings.searchRadiusKm },
            controller.signal,
          ),
          api.createPlan({ start, settings }, controller.signal),
        ]);

        if (controller.signal.aborted) return;

        applyBundle(bundle, {
          cars: cars.cars,
          rejectedCount: cars.rejected.length,
          fetchedAtISO: cars.fetchedAtISO,
          mock: bundle.mock || cars.mock,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setData((prev) => ({
          ...prev,
          status: 'error',
          error:
            error instanceof ApiRequestError
              ? error
              : new ApiRequestError(0, 'unknown', 'Неизвестная ошибка'),
        }));
      }
    },
    [applyBundle],
  );

  const mark = useCallback(
    async (index: number, kind: 'done' | 'skipped') => {
      const planId = planIdRef.current;
      const nextDone = new Set(done);
      const nextSkipped = new Set(skipped);
      if (kind === 'done') {
        nextDone.add(index);
        nextSkipped.delete(index);
      } else {
        nextSkipped.add(index);
        nextDone.delete(index);
      }
      // Оптимистично: пользователь стоит у колонки, ждать сеть незачем.
      setDone(nextDone);
      setSkipped(nextSkipped);
      persist(nextDone, nextSkipped);

      if (!planId) return;
      const bundle =
        kind === 'done'
          ? await api.completeStop(planId, index)
          : await api.skipStop(planId, index);
      // Сервер мог перестроить хвост цепочки — принимаем его версию целиком.
      if (bundle) applyBundle(bundle);
    },
    [done, skipped, persist, applyBundle],
  );

  const markDone = useCallback((index: number) => mark(index, 'done'), [mark]);
  const markSkipped = useCallback((index: number) => mark(index, 'skipped'), [mark]);

  const replacePlan = useCallback(
    (plan: Plan) => {
      setData((prev) => ({
        ...prev,
        plan,
        stations: prev.stations,
        status: 'ready',
        error: null,
      }));
      if (plan.id !== planIdRef.current) {
        planIdRef.current = plan.id;
        setDone(new Set());
        setSkipped(new Set());
        writeProgress({ planId: plan.id, done: [], skipped: [] });
      }
    },
    [],
  );

  const currentIndex =
    data.plan?.stops.find(
      (stop) => !done.has(stop.index) && !skipped.has(stop.index),
    )?.index ?? null;

  return {
    ...data,
    done,
    skipped,
    restoring,
    currentIndex,
    load,
    restore,
    markDone,
    markSkipped,
    replacePlan,
  };
}
